/**
 * Setting up a tournament: the one way a new tournament and its rounds are made.
 *
 * The Tournament Builder (/prioritization/tournaments/new) and the cycle configure
 * wizard's Tournament step both create through `createTournamentWithRounds`, which
 * calls the tournament store's `createTournament` and then `addRound` for each
 * round. The tournament is left in DRAFT: nothing here marks it ready or opens a
 * round. That happens on the tournament's own page.
 */
import type { CreateTournamentInput, CreateTournamentRoundInput, ParticipantMode, Tournament } from '../types/prioritization/tournament';

export const RANKING_TOOLS = [
  { id: 'stack_ranking',          label: 'Stack Ranking',       desc: 'Drag to rank items in order' },
  { id: 'pairwise_ranking',       label: 'Pairwise Ranking',    desc: 'Compare items head-to-head' },
  { id: 'dot_voting',             label: 'Dot Voting',          desc: 'Allocate votes across items' },
  { id: 'buy_a_feature',          label: 'Buy a Feature',       desc: 'Spend a budget on features' },
  { id: 'impact_effort_matrix',   label: 'Impact-Effort Matrix',desc: '2×2 quadrant filtering' },
  { id: 'wsjf',                   label: 'WSJF',                desc: 'Weighted Shortest Job First' },
  { id: 'rice',                   label: 'RICE Scoring',        desc: 'Reach · Impact · Confidence · Effort' },
  { id: 'moscow',                 label: 'MoSCoW',              desc: 'Must Have / Should Have / Could Have / Won\'t' },
  { id: 'simple_weighted_scoring',label: 'Weighted Scoring',    desc: 'Custom multi-criteria scoring' },
] as const;

export interface RoundConfig {
  label: string;
  advancingCount: number;
}

export const DEFAULT_ROUND_LABELS: Record<number, string[]> = {
  2: ['Semifinals', 'Finals'],
  3: ['Quarterfinals', 'Semifinals', 'Finals'],
  4: ['Round of 32', 'Quarterfinals', 'Semifinals', 'Finals'],
  5: ['Round 1', 'Round 2', 'Quarterfinals', 'Semifinals', 'Finals'],
};

/** `count` rounds with their usual names, each advancing about half of what it receives. */
export function defaultRounds(count: number, totalItems: number): RoundConfig[] {
  const labels = DEFAULT_ROUND_LABELS[count] ?? Array.from({ length: count }, (_, i) => `Round ${i + 1}`);
  return labels.map((label, i) => ({
    label,
    advancingCount: Math.max(1, Math.ceil(totalItems / Math.pow(2, i + 1))),
  }));
}

/**
 * The round counts that make sense for `totalItems`: those whose final still
 * receives at least two items to rank. Four items run in two rounds at most —
 * a third would hand the final one item. Fewer than three items fit none.
 */
export function usefulRoundCounts(totalItems: number): number[] {
  return Object.keys(DEFAULT_ROUND_LABELS)
    .map(Number)
    .filter((count) => Math.ceil(totalItems / Math.pow(2, count - 1)) >= 2);
}

/** The usual three rounds, or the most that still make sense for `totalItems`. */
export function defaultRoundCount(totalItems: number): number {
  const useful = usefulRoundCounts(totalItems);
  return useful.includes(3) ? 3 : useful[useful.length - 1] ?? 2;
}

export interface TournamentSetup {
  name: string;
  description?: string;
  cycleId?: string;
  trackId?: string;
  facilitatorId: string;
  itemIds: string[];
  toolId: string;
  rounds: RoundConfig[];
  participantMode: ParticipantMode;
  /** For participantMode 'fixed'. */
  defaultParticipantIds?: string[];
  /** For participantMode 'per_round', one list per round. */
  perRoundParticipantIds?: string[][];
}

export interface TournamentWriter {
  createTournament: (input: CreateTournamentInput) => Promise<Tournament>;
  addRound: (input: CreateTournamentRoundInput) => Promise<unknown>;
}

/** Create a DRAFT tournament and its rounds. Throws when the store refuses a write. */
export async function createTournamentWithRounds(setup: TournamentSetup, store: TournamentWriter): Promise<Tournament> {
  const sourcePoolId = `tournament-source-${crypto.randomUUID()}`;
  const tournament = await store.createTournament({
    name: setup.name.trim(),
    description: setup.description?.trim() || undefined,
    trackId: setup.trackId,
    cycleId: setup.cycleId,
    config: {
      sourceItemPoolId: sourcePoolId,
      advancementRule: { type: 'top_k', k: setup.rounds[0]?.advancingCount ?? 1 },
      criteriaMode: 'per_round',
      toolMode: 'inherited',
      defaultToolId: setup.toolId,
      defaultToolConfig: {},
      participantMode: setup.participantMode,
      defaultParticipantIds: setup.participantMode === 'fixed' ? (setup.defaultParticipantIds ?? []) : [],
      autoAdvance: false,
      notifyOnRoundOpen: true,
      notifyOnRoundComplete: true,
      captureEliminationRationale: true,
    },
    facilitatorId: setup.facilitatorId,
    workspaceId: undefined,
    itemIds: setup.itemIds,
  });

  for (let i = 0; i < setup.rounds.length; i++) {
    const round = setup.rounds[i];
    await store.addRound({
      tournamentId: tournament.id,
      roundNumber: i + 1,
      label: round.label || `Round ${i + 1}`,
      itemPoolId: `tournament-pool-${tournament.id}-r${i + 1}`,
      advancingK: round.advancingCount,
      toolId: setup.toolId,
      toolConfig: {},
      participantIds: setup.participantMode === 'per_round' ? (setup.perRoundParticipantIds?.[i] ?? []) : undefined,
      prerequisites: i === 0 ? [] : [{ type: 'PREVIOUS_ROUND_COMPLETE' }],
    });
  }
  return tournament;
}
